import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import Grid from "@mui/material/Grid2";
import ImageList from "@mui/material/ImageList";
import ImageListItem from "@mui/material/ImageListItem";
import Typography from "@mui/material/Typography";

import { useGridApiContext } from "@mui/x-data-grid-pro";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import ImageTile from "./ImageTile";
import { useDownloadMedia } from "@/hooks/media/useDownloadMedia";
import { useMediaFile } from "@/hooks/media/useMediaFile";
import { MaintenanceDisplayData } from "@/models/MaintenanceDisplayData";

export type MediaItem = {
  id: string;
  url: string;
  contentType?: string;
  filename?: string;
  name?: string;
};

export default function MaintenanceRowDetail({ row }: { row: MaintenanceDisplayData }) {
  const { t } = useTranslation();

  const mediaIds = useMemo(
    () => (row?.workOrderJob?.mediaFiles ?? []).map(f => f.id).filter(Boolean),
    [row?.workOrderJob?.mediaFiles],
  );

  const { data: blobs, isLoading, error } = useDownloadMedia(mediaIds);
  const { data: nameData = [] } = useMediaFile(mediaIds);

  const nameById = useMemo(() => {
    const map = new Map<string, { name?: string }>();
    for (const m of nameData ?? []) {
      map.set(m.id, { name: (m?.metaData as any)?.name });
    }
    return map;
  }, [nameData]);

  const items: MediaItem[] = useMemo(() => {
    if (!blobs) return [];
    return blobs.map((m: any) => {
      const blob: Blob = m.blob ?? m;
      const url = URL.createObjectURL(blob);
      const meta = nameById.get(m.id ?? "");
      const filename = meta?.name || m.filename || m.id || "media";
      return {
        id: m.id ?? crypto.randomUUID(),
        url,
        contentType: m.contentType ?? blob.type,
        filename,
        name: meta?.name,
      };
    });
  }, [blobs, nameById]);

  useEffect(() => {
    return () => {
      items.forEach(i => URL.revokeObjectURL(i.url));
    };
  }, [items]);

  // split into defect marker vs. others (check name or filename)
  const { defect, others } = useMemo(() => {
    const isDefect = (mi: MediaItem) => {
      const key = (mi.name || mi.filename || mi.id).toLowerCase();
      return key.startsWith("defect_marker");
    };
    const defect = items.find(isDefect) ?? null;
    const others = items.filter(i => i !== defect);
    return { defect, others };
  }, [items]);

  // width stuff neccessary to have the right width inside of geotab view
  const apiRef = useGridApiContext();

  const [width, setWidth] = useState(() => {
    const dimensions = apiRef.current.getRootDimensions();
    return dimensions?.viewportOuterSize.width;
  });

  const handleViewportInnerSizeChange = useCallback(() => {
    const dimensions = apiRef.current.getRootDimensions();
    setWidth(dimensions?.viewportOuterSize.width);
  }, [apiRef]);

  useEffect(() => {
    return apiRef.current.subscribeEvent("viewportInnerSizeChange", handleViewportInnerSizeChange);
  }, [apiRef, handleViewportInnerSizeChange]);

  return (
    <Box sx={{ position: "sticky", left: 0, width, height: "auto" }}>
      {/* Loading / Error */}
      {isLoading && (
        <Box display="flex" alignItems="center" gap={1} mb={2}>
          <CircularProgress size={18} />
          <Typography variant="body2">{t("maintenance.media.loading")}</Typography>
        </Box>
      )}
      {error && (
        <Typography variant="body2" color="error" mb={2}>
          {t("maintenance.media.loadError")}
        </Typography>
      )}

      {/* Empty */}
      {!isLoading && !error && items.length === 0 && (
        <>
          {row?.workOrderJob?.eventDescription && (
            <DescriptionBlock
              title={t("maintenance.eventDescription")}
              text={row.workOrderJob.eventDescription}
            />
          )}
          <Typography variant="body2" color="text.secondary" padding={5} paddingTop={1}>
            {t("maintenance.media.none")}
          </Typography>
        </>
      )}

      {/* Content */}
      {!isLoading && !error && items.length > 0 && (
        <Grid container columns={9}>
          <Grid size={3} sx={{ maxHeight: "75vh", overflow: "auto", padding: 5, paddingTop: 1 }}>
            {defect ? (
              <ImageTile item={defect} dense />
            ) : (
              <Typography variant="body2" color="text.secondary">
                {t("maintenance.media.none")}
              </Typography>
            )}
          </Grid>

          <Grid size={3} sx={{ maxHeight: "75vh", overflow: "auto" }}>
            {
              // @ts-ignore
              row?.workOrderJob?.description ? (
                <DescriptionBlock
                  title={t("maintenance.eventDescription")}
                  // @ts-ignore
                  text={row.workOrderJob.description}
                />
              ) : (
                <Typography variant="body2" color="text.secondary">
                  {t("maintenance.media.none")}
                </Typography>
              )
            }
          </Grid>

          <Grid size={3} sx={{ maxHeight: "75vh", overflow: "auto", paddingTop: 1 }}>
            {others.length > 0 ? (
              <TightImageGrid items={others} />
            ) : (
              <Typography variant="body2" color="text.secondary">
                {t("maintenance.media.none")}
              </Typography>
            )}
          </Grid>
        </Grid>
      )}
    </Box>
  );
}

function DescriptionBlock({ title, text }: { title: string; text: string }) {
  return (
    <Box padding={5} paddingTop={1}>
      <Typography variant="subtitle2" gutterBottom>
        {title}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: "pre-wrap" }}>
        {text}
      </Typography>
    </Box>
  );
}

function TightImageGrid({ items }: { items: MediaItem[] }) {
  return (
    <ImageList variant="masonry" cols={1} gap={10}>
      {items.map(item => (
        <ImageListItem key={item.id} sx={{ overflow: "hidden", borderRadius: 1 }}>
          <ImageTile item={item} />
        </ImageListItem>
      ))}
    </ImageList>
  );
}
